import { WoltLogo, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { fetchMerchants } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { fetchVenues } from 'apis/restaurant-api/merchant-admin/venue/api';
import { FeatureType } from 'common/types';
import SearchItemSelector from 'modules/search-item-selector/SearchItemSelector';

import shouldShowVenueSelector from './shouldShowVenueSelector';
import { hasPermissions, usePermissions } from '../../../common/wolt-permissions';
import UserDropdown from '../components/UserDropdown';

export type Props = {
  selectedVenueSlug?: string;
  selectedFeature?: FeatureType;
  hideVenueSelector: boolean;
};

const Root = styled.div`
  position: relative;
  display: flex;
  height: 48px;
  min-height: 48px;
  padding: 0 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  background-color: ${cbSemanticCssVars.surfaceMain};
  max-width: 100%;
  z-index: 1000;
  align-items: center;

  @media (width <= 540px) {
    padding: 0 18px;
  }
`;

const StyledWoltLogo = styled(WoltLogo)`
  height: 16px;
  fill: ${cbSemanticCssVars.textPrimary};
`;

const Left = styled.div`
  flex: 0 0 auto;
  width: 75px;
`;

const LogoLink = styled(Link)`
  display: block;
`;

const Right = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: 75px;
`;

const Center = styled.div`
  flex: 1 1 auto;
  display: flex;
  min-width: 0;

  @media (width <= 640px) {
    justify-content: center;
  }
`;

const Header = ({ selectedVenueSlug, selectedFeature, hideVenueSelector }: Props) => {
  const permissions = usePermissions();
  const { pathname } = useLocation();
  const isQueryEnabled = selectedFeature !== 'identity-verification' && !hideVenueSelector;

  // TODO: remove ref logic after serve sign up form is moved to public route
  const rootRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Added for auto scroll to top when changing between self serve sign up routes
    rootRef.current && rootRef.current.scrollIntoView();
  }, [pathname]);

  const { data: merchants = [] } = useQuery(['/v1/merchant-admin/merchants'], () => fetchMerchants(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isQueryEnabled,
  });

  const { data: venues = [] } = useQuery(['/v1/merchant-admin/venues'], () => fetchVenues(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isQueryEnabled,
  });

  const showVenueSelector = useMemo(() => {
    if (hideVenueSelector) {
      return false;
    }
    const isEmployee = hasPermissions(
      [
        { resource: 'merchant', operations: ['r'] },
        { resource: 'restaurant', operations: ['r'] },
      ],
      permissions,
    );
    const hasVenuesVenueGroupsOrMerchants = venues.length > 0 || merchants.length > 0;
    return shouldShowVenueSelector(selectedFeature, hasVenuesVenueGroupsOrMerchants, isEmployee);
  }, [merchants.length, venues.length, selectedFeature, hideVenueSelector, permissions]);

  const logoLinkTo = selectedFeature === 'identity-verification' ? '/identity-verification' : '';

  return (
    <Root ref={rootRef}>
      <Left>
        <LogoLink to={logoLinkTo}>
          <StyledWoltLogo />
        </LogoLink>
      </Left>

      <Center>
        {showVenueSelector && (
          <SearchItemSelector selectedSlug={selectedVenueSlug} selectedFeature={selectedFeature} isHorizontalBar />
        )}
      </Center>

      <Right>
        <UserDropdown />
      </Right>
    </Root>
  );
};

export default Header;
